import React from 'react';
import { t, defineMessages } from '@sm/intl';
import { PollingExperiment, PollingExperiments } from './StandardEndpage/pollingExperiments';
import EndpageTemplates from './EndpageTemplates';
import Embedded from './Embedded';
import EmbeddedNonBrandedHeader from '~app/components/EmbeddedNonBrandedHeader';
import NonBranded from './NonBranded';
import RebrandEpForm from './RebrandEpForm';
import RebrandEpFormIntl from './RebrandEpFormIntl';
import Research from './Research';
import StandardEndpage from './StandardEndpage';
import { GUAC_EXISTING_USER_PAGES, US_GEO_PAGES, GAC_GEO_PAGES, WeightedElement } from './constants';

/**
 * Using a weighted distribution, select and return the page that the target element falls on
 *
 * @param weightedElements object of pages
 * @param target value within distribution range, use a random number [0, 1) if weighted distribution adds up to 1
 * @returns Selected page to serve
 */
export default function WeightedElementSelector(
  weightedElements: WeightedElement[],
  target: number
): React.ReactElement {
  let cumulativeWeight = 0.0;
  let selectedPage = <StandardEndpage pageName="CxEndpage" utSource="survey-thanks" />;

  weightedElements.every(element => {
    cumulativeWeight += element.weight;
    if (target < cumulativeWeight) {
      selectedPage = element.page;
      return false;
    }
    return true;
  });

  return selectedPage;
}

export function checkWeightsEqualToOne(weightedElements: WeightedElement[]): boolean {
  let total = 0.0;
  weightedElements.forEach(element => {
    total += element.weight;
  });
  if (Number(total.toFixed(3)) === 1) return true;
  return false;
}

/**
 * Iterates through the experiments listed in PollingExperiments object  to see if any match the page's experiment.
 * @returns an Endpage
 */
export function renderPolling(
  alternate: React.ReactElement,
  pollingTreatment: string,
  pollingExperiment: string
): React.ReactElement {
  let toReturn = alternate;
  let pageName = '';
  PollingExperiments.every((element: PollingExperiment) => {
    if (pollingExperiment === element.experimentName) {
      pageName = element.pageName;
      return false;
    }
    return true;
  });
  if (pollingTreatment && pollingExperiment) {
    toReturn = <StandardEndpage pageName={pageName} treatmentName={pollingTreatment} utSource="survey-thanks" />;
  }
  return toReturn;
}

type pollingCookies = [
  cookie: {
    age: number;
    name: string;
    value: string;
  }
];

/**
 * If the polling experiment comes back with experiment and polling cookie
 * values to save, do that. This is mostly for logged out users, but can
 * also apply to experiment preview cookies.
 *
 * @param {*} pollingCookies The polling query cookie object returned from experimentsvc
 */
export function savePollingExperimentCookies(cookies: pollingCookies): void {
  // Set each cookie the same way it's done in <Treatment>
  const { host } = window.location;
  const domain = host.replace(/^[^.]+\./g, '');
  cookies.forEach(cookieObj => {
    document.cookie = `${cookieObj.name}=${cookieObj.value}; max-age=${cookieObj.age}; path=/; domain=${domain}; secure=true; SameSite=None`;
  });
}

const COPY = defineMessages({
  DEFAULT_THANKS_MESSAGE: {
    id: 'SurveyThanks.utils.thanksMessage',
    defaultMessage: 'Thank you for taking this survey.',
  },
});

type pageResult = {
  Endpage: React.ReactElement;
  potentialPollingEndpage: boolean;
};

export function pageFunnel(
  isSmReleaseToggles: boolean,
  isEmbedded: boolean,
  isGovernment: boolean,
  isWhiteLabel: boolean,
  nonBranded: boolean,
  isNonEnglish: boolean,
  isBasicEndpageGroup: boolean,
  isExistingUser: boolean,
  isUSA: boolean,
  isGAC: boolean,
  randomNumber: number
): pageResult {
  let potentialPollingEndpage = false;
  let Endpage: React.ReactElement;
  switch (true) {
    case isSmReleaseToggles:
      potentialPollingEndpage = true;
      Endpage = <EndpageTemplates />;
      break;
    case isEmbedded:
      Endpage = nonBranded ? (
        <EmbeddedNonBrandedHeader message={t(COPY.DEFAULT_THANKS_MESSAGE)} icon="thanks" />
      ) : (
        <Embedded />
      );
      break;
    case isGovernment:
      Endpage = <NonBranded />;
      break;
    case isWhiteLabel:
      Endpage = <Research />;
      break;
    case nonBranded:
      Endpage = <NonBranded />;
      break;
    case isNonEnglish:
      potentialPollingEndpage = true;
      Endpage = <RebrandEpFormIntl />;
      break;
    case isBasicEndpageGroup:
      Endpage = <RebrandEpForm />;
      break;
    case isExistingUser && (isUSA || isGAC):
      potentialPollingEndpage = true;
      Endpage = WeightedElementSelector(GUAC_EXISTING_USER_PAGES, randomNumber);
      break;
    case isUSA:
      potentialPollingEndpage = true;
      Endpage = WeightedElementSelector(US_GEO_PAGES, randomNumber);
      break;
    case isGAC:
      potentialPollingEndpage = true;
      Endpage = WeightedElementSelector(GAC_GEO_PAGES, randomNumber);
      break;
    default:
      Endpage = <RebrandEpForm />;
      break;
  }
  return { Endpage, potentialPollingEndpage };
}
